import {
  CheckCircle2,
  LockKeyhole,
  PackageCheck,
  PlugZap,
  ShieldCheck,
  Truck,
  UsersRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import heroDeliveryCommerce from '@/assets/hero/hero-coli-noki.webp';

const trustBadges = [
  { label: 'Paiement sécurisé', icon: LockKeyhole },
  { label: 'Données protégées', icon: ShieldCheck },
  { label: 'Support réactif', icon: UsersRound },
  { label: 'Intégration facile', icon: PlugZap },
];

const notifications = [
  {
    title: 'Commande passée',
    message: 'Votre commande est confirmée !',
    icon: CheckCircle2,
    tone: 'text-red-600 bg-red-50 border-red-100',
    className: 'right-2 top-2 sm:right-10 sm:top-8 lg:right-0 lg:top-14',
  },
  {
    title: 'En livraison',
    message: 'Votre colis est en route vers vous',
    icon: Truck,
    tone: 'text-red-600 bg-red-50 border-red-100',
    className: 'hidden md:block md:left-0 md:top-[56%] lg:-left-12 lg:top-[54%]',
  },
  {
    title: 'Livraison réussie',
    message: 'Votre colis a été livré avec succès !',
    icon: PackageCheck,
    tone: 'text-emerald-600 bg-emerald-50 border-emerald-100',
    className: 'bottom-2 left-2 sm:bottom-10 sm:right-10 sm:left-auto lg:bottom-20 lg:right-2',
  },
];

const DeliveryRouteDecoration = () => (
  <div className="pointer-events-none absolute inset-x-0 top-6 z-20 hidden sm:block lg:-left-5 lg:right-0 lg:top-12">
    <svg
      viewBox="0 0 700 285"
      aria-hidden="true"
      className="h-auto w-full text-red-500 opacity-45 hero-route-line"
      fill="none"
    >
      <path
        d="M64 196 C156 82 268 240 390 116 C482 26 578 74 636 34"
        stroke="currentColor"
        strokeWidth="2.35"
        strokeLinecap="round"
        strokeDasharray="8 12"
      />
      <g className="hero-route-pin" fill="currentColor">
        <path d="M64 175c-12 0-21 9-21 21 0 16 21 39 21 39s21-23 21-39c0-12-9-21-21-21Zm0 28a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" />
        <path d="M390 95c-12 0-21 9-21 21 0 16 21 39 21 39s21-23 21-39c0-12-9-21-21-21Zm0 28a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" />
        <path d="M636 13c-12 0-21 9-21 21 0 16 21 39 21 39s21-23 21-39c0-12-9-21-21-21Zm0 28a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" />
      </g>
    </svg>
  </div>
);

const HeroSection = ({ onCreateAccount, onTrackPackage }) => (
  <section className="relative overflow-hidden bg-white max-md:overflow-x-hidden">
    <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-slate-50 to-white" />

    <div className="relative mx-auto max-w-7xl px-4 pb-3 pt-4 sm:px-6 sm:pb-5 sm:pt-6 lg:px-8 lg:pb-3 lg:pt-5">
      <div className="grid items-start gap-6 max-md:grid-cols-1 lg:grid-cols-2 lg:gap-8">
        <div className="mx-auto max-w-xl text-center hero-copy-enter lg:mx-0 lg:pt-8 lg:text-left xl:pt-10">
          <h1 className="hero-mobile-title w-full max-w-full break-words font-bold tracking-tight text-slate-950 sm:text-[2.75rem] lg:text-[2.85rem] xl:text-[3.18rem]">
            <span className="block md:hidden">Vous vendez.</span>
            <span className="block md:hidden">On s’occupe du reste.</span>
            <span className="block text-red-600 md:hidden">Livraison simple,</span>
            <span className="block text-red-600 md:hidden">clients satisfaits.</span>
            <span className="hidden md:block">Vous vendez.</span>
            <span className="hidden md:block">On s’occupe du reste.</span>
            <span className="hidden text-red-600 md:block">Livraison simple, clients satisfaits.</span>
          </h1>

          <p className="hero-mobile-copy mx-auto mt-4 w-full max-w-[31rem] text-[0.92rem] leading-6 text-slate-600 sm:text-base sm:leading-7 lg:mx-0 lg:max-w-[29rem]">
            Coli Noki simplifie vos ventes au quotidien : vous gérez vos clients, on gère la livraison. Moins de tracas, plus de temps pour développer votre business.
          </p>

          <div className="hero-mobile-buttons mt-5 flex max-w-full flex-wrap justify-center gap-2 sm:gap-2.5 lg:justify-start">
            <Button
              data-testid="track-package-btn"
              size="lg"
              variant="outline"
              className="hero-mobile-button hero-mobile-button-secondary h-9 max-w-[10.5rem] rounded-lg border-slate-200 bg-white px-3.5 text-[0.82rem] font-semibold text-slate-900 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-red-200 hover:bg-red-50 hover:text-red-700 min-[360px]:px-4 sm:h-10 sm:px-5 sm:text-sm"
              onClick={onTrackPackage}
            >
              Suivre un colis
            </Button>
            <Button
              data-testid="scroll-to-auth-btn"
              size="lg"
              className="hero-mobile-button hero-mobile-button-primary h-9 max-w-[12.5rem] rounded-lg bg-red-600 px-3.5 text-[0.82rem] font-semibold text-white shadow-lg shadow-red-600/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-red-600/30 min-[360px]:px-4 sm:h-10 sm:px-5 sm:text-sm"
              onClick={onCreateAccount}
            >
              Commencer maintenant
            </Button>
          </div>

          <div className="hero-mobile-badges -mx-4 mt-5 flex max-w-full flex-nowrap gap-1.5 overflow-x-auto px-4 pb-2 hero-badges-scroll sm:mx-0 sm:justify-center sm:px-0 lg:max-w-[34rem] lg:justify-start lg:gap-1">
            {trustBadges.map(({ label, icon: Icon }) => (
              <div
                key={label}
                className="hero-mobile-badge inline-flex min-w-max shrink-0 items-center justify-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[0.66rem] font-medium text-slate-700 shadow-sm min-[360px]:gap-1.5 min-[360px]:text-[0.68rem] sm:min-w-0 sm:px-2 sm:text-[0.7rem] lg:px-2"
              >
                <Icon className="h-3 w-3 shrink-0 text-red-600" />
                <span className="hero-mobile-badge-label whitespace-nowrap">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-visual-enter">
          <div className="hero-mobile-visual relative mx-auto w-full max-w-[500px] overflow-visible px-1 sm:max-w-[720px] sm:px-6 lg:ml-auto lg:mr-[-2rem] lg:max-w-[760px] lg:px-0 xl:mr-[-4rem] xl:max-w-[820px]">
            <DeliveryRouteDecoration />

            <div className="hero-mobile-image-wrap relative z-10 flex min-h-[300px] w-full items-center justify-center sm:min-h-[520px] lg:min-h-[610px] xl:min-h-[660px]">
              <img
                src={heroDeliveryCommerce}
                alt="Livreur Coli Noki et cliente e-commerce recevant une confirmation de commande"
                className="hero-mobile-image h-[300px] w-full max-w-[500px] object-contain sm:h-[520px] sm:max-w-none lg:h-[610px] xl:h-[660px]"
              />
            </div>

            {notifications.map(({ title, message, icon: Icon, tone, className }, index) => (
              <div
                key={title}
                className={`hero-mobile-notification hero-floating-card absolute z-30 w-[min(42vw,150px)] rounded-xl border bg-white/90 p-1.5 text-left shadow-[0_18px_50px_-24px_rgba(15,23,42,0.55)] backdrop-blur-md min-[380px]:w-[min(40vw,160px)] sm:w-52 sm:p-2.5 lg:w-56 ${className}`}
                style={{ animationDelay: `${index * 0.55}s` }}
              >
                <div className="flex items-start gap-1.5 sm:gap-2.5">
                  <div className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border sm:h-8 sm:w-8 ${tone}`}>
                    <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  </div>
                  <div>
                    <p className="text-[0.62rem] font-semibold leading-4 text-slate-950 min-[380px]:text-[0.68rem] sm:text-[0.82rem]">{title}</p>
                    <p className="mt-0.5 text-[0.58rem] leading-3.5 text-slate-600 min-[380px]:text-[0.62rem] sm:text-[0.72rem] sm:leading-5">{message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default HeroSection;
